import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import menuService from '../services/menuService'
import cuisineService, { type CuisineDish } from '../services/cuisineService'
import { Restaurant } from '../types/restaurant'
import { useTranslation } from '../hooks/use-i18n'

interface CreateMenuModalProps {
  restaurantId: string
  restaurant: Restaurant
  onClose: () => void
  onSuccess: () => void
}

interface CreateMenuForm {
  name: string
}

const CreateMenuModal: React.FC<CreateMenuModalProps> = ({
  restaurantId,
  restaurant,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [dishes, setDishes] = useState<CuisineDish[]>([])
  const [dishesLoading, setDishesLoading] = useState(true)
  const [selectedDishIds, setSelectedDishIds] = useState<string[]>([])
  const { register, handleSubmit, formState: { errors } } = useForm<CreateMenuForm>()

  useEffect(() => {
    const loadDishes = async () => {
      const cuisineIds = restaurant.cuisines?.map((c) => c.id) || []
      const allDishes: CuisineDish[] = []
      for (const cid of cuisineIds) {
        try {
          const categoriesWithDishes = await cuisineService.getDishesByCuisine(cid)
          for (const cat of categoriesWithDishes) {
            for (const d of cat.dishes || []) {
              if (!allDishes.find((x) => x.id === d.id)) allDishes.push(d)
            }
          }
        } catch {
          // skip failed cuisine
        }
      }
      setDishes(allDishes)
      setDishesLoading(false)
    }
    loadDishes()
  }, [restaurant.cuisines])

  const handleDishChange = (dishId: string, checked: boolean) => {
    if (checked) {
      setSelectedDishIds((prev) => [...prev, dishId])
    } else {
      setSelectedDishIds((prev) => prev.filter((id) => id !== dishId))
    }
  }

  const onSubmit = async (data: CreateMenuForm) => {
    if (selectedDishIds.length === 0) {
      toast.error(t('toast.selectDishRequired'))
      return
    }
    try {
      setIsLoading(true)
      await menuService.createMenu({
        name: data.name,
        restaurantId,
        type: 'custom',
        dishIds: selectedDishIds,
      })
      toast.success(t('toast.menuCreated'))
      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.menuCreateFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="blue-sky-card rounded-3xl p-8 w-full max-w-2xl mx-4 shadow-2xl animate-scale-in my-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold text-white">{t('menu.create.title')}</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">{t('menu.create.nameLabel')}</label>
            <input
              {...register('name', { required: t('menu.create.validation.nameRequired') })}
              type="text"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300"
              placeholder={t('menu.create.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-2 text-sm text-red-400">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-3">
              {t('menu.create.dishesLabel')}
            </label>
            {dishesLoading ? (
              <div className="py-8 text-center text-white/70">{t('menu.create.loadingDishes')}</div>
            ) : (
              <>
                <div className="max-h-96 overflow-y-auto bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {dishes.map((dish) => (
                      <label
                        key={dish.id}
                        className={`flex items-start p-3 rounded-lg transition-colors cursor-pointer ${
                          selectedDishIds.includes(dish.id)
                            ? 'bg-sky-500/20 border-2 border-sky-500'
                            : 'bg-white/5 border-2 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDishIds.includes(dish.id)}
                          onChange={(e) => handleDishChange(dish.id, e.target.checked)}
                          className="mt-1 mr-3 w-4 h-4 text-sky-500 bg-white/10 border-white/20 rounded focus:ring-sky-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-white block">
                            {dish.name}
                            {dish.region ? ` (${dish.region})` : ''}
                          </span>
                          {dish.ingredients && dish.ingredients.length > 0 && (
                            <span className="text-xs text-white/70 mt-1 block line-clamp-2">
                              {dish.ingredients.map((i) => i.name).join(', ')}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                {selectedDishIds.length === 0 && (
                  <p className="mt-2 text-sm text-yellow-400">{t('menu.create.selectDishWarning')}</p>
                )}
                {selectedDishIds.length > 0 && (
                  <p className="mt-2 text-sm text-white/70">
                    {t('menu.create.selectedDishes', { count: String(selectedDishIds.length), label: selectedDishIds.length === 1 ? t('common.dishSingular') : t('common.dishes') })}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-white/30 text-white rounded-xl hover:bg-white/10 transition-all duration-300 font-semibold"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || selectedDishIds.length === 0 || dishesLoading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl font-semibold"
            >
              {isLoading ? t('menu.create.submitting') : t('menu.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateMenuModal
